import Button from "@/components/Button";
import MobPlusIndicator from "@/components/MobPlusIndicator";
import ExpandableSummary from "@/src/components/ExpandableSummary";
import MainHeading from "@/src/components/MainHeading";
import Meta from "@/src/recipes/components/RecipeHero/Meta";
import MiniChefBio from "@/src/recipes/components/RecipeHero/MiniChefBio";
import RecipeActions from "@/src/recipes/components/RecipeHero/RecipeActions";
import SignUpCTAButton from "@/src/recipes/components/RecipeHero/SignUpCTAButton";
import { MealPlan } from "@/types/mealPlan";
import { Recipe } from "@/types/recipe";
import clsx from "clsx";
import { observer } from "mobx-react";
import Link from "next/link";
import React from "react";
import { useInView } from "react-intersection-observer";

const DetailsPanel = observer(
  ({
    entry,
    contentIsRestricted,
    isMobPlusMember,
    ratingValue,
    totalNotesIncludingReplies,
    theme = "light" as "light" | "dark",
    nextRecipeHref = null,
    prevRecipeHref = null,
    mealPlan = null,
  }: {
    entry: Recipe;
    contentIsRestricted: boolean;
    isMobPlusMember: boolean;
    ratingValue: number;
    totalNotesIncludingReplies: number;
    theme: "light" | "dark";
    nextRecipeHref: string | null;
    prevRecipeHref: string | null;
    mealPlan: MealPlan | null;
  }) => {
    const { ref, inView } = useInView({
      triggerOnce: true,
    });

    const hideChefBio =
      !entry.chefs ||
      entry.chefs.length === 0 ||
      (entry.chefs[0] && entry.chefs[0].title === "mob");

    const baseDelay = 0;
    const delayIncrement = 50;

    const getDelay = (index: number) => {
      const adjustedIndex = !!mealPlan ? index : index - 1;
      return `${baseDelay + delayIncrement * Math.max(0, adjustedIndex)}ms`;
    };

    return (
      <div
        className={clsx(
          "RecipeHero__details",
          "flex flex-col p-6 print:!container xs:py-8 lg:justify-end lg:p-12 print:!py-6"
        )}
        ref={ref}
      >
        <div>
          {contentIsRestricted && (
            <div
              className={clsx(
                "RecipeHero__mobPremiumBadge",
                "mb-2 hidden items-center justify-center gap-x-2 transition duration-700 ease-out sm:justify-start md:mb-4 lg:flex print:!hidden",
                {
                  "translate-y-2 opacity-0": !inView,
                  "translate-y-0 opacity-100": inView,
                }
              )}
              style={{ transitionDelay: getDelay(0) }}
            >
              <MobPlusIndicator
                locked={!isMobPlusMember}
                theme={theme}
                border
                caption="Unlock this recipe"
              />
            </div>
          )}
          <div className={clsx("space-y-4 md:space-y-6")}>
            {!!mealPlan && (
              <div
                className={clsx("flex items-start space-x-3 font-body text-sm")}
              >
                <Link
                  href={`/${mealPlan?.uri}`}
                  className={clsx(
                    "font-medium hover:opacity-50",
                    "transition duration-700 ease-out",
                    {
                      "translate-y-2 opacity-0": !inView,
                      "translate-y-0 opacity-100": inView,
                    }
                  )}
                  style={{ transitionDelay: getDelay(1) }}
                >
                  {mealPlan.title}
                </Link>
                {(!!prevRecipeHref || !!nextRecipeHref) && (
                  <>
                    <div className="h-5 w-px bg-current opacity-20"></div>
                    <div className="flex items-center space-x-2">
                      {!!prevRecipeHref && (
                        <Link
                          href={`/${prevRecipeHref}`}
                          className={clsx(
                            "text-zinc-500 transition ease-out hover:text-zinc-950"
                          )}
                        >
                          Previous
                        </Link>
                      )}
                      {!!nextRecipeHref && (
                        <Link
                          href={`/${nextRecipeHref}`}
                          className={clsx(
                            "text-zinc-500 transition ease-out hover:text-zinc-950"
                          )}
                        >
                          Next
                        </Link>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            <MainHeading
              className={clsx(
                "RecipeHero__heading",
                "transition duration-700 ease-out",
                {
                  "translate-y-2 opacity-0": !inView,
                  "translate-y-0 opacity-100": inView,
                }
              )}
              style={{ transitionDelay: getDelay(2) }}
            >
              {entry.title}
            </MainHeading>

            <div
              className={clsx("transition duration-700 ease-out", {
                "translate-y-2 opacity-0": !inView,
                "translate-y-0 opacity-100": inView,
              })}
              style={{ transitionDelay: getDelay(3) }}
            >
              <Meta
                recipe={entry}
                ratingValue={ratingValue}
                totalNotesIncludingReplies={totalNotesIncludingReplies}
              />
            </div>

            <div
              className={clsx("transition duration-700 ease-out", {
                "translate-y-2 opacity-0": !inView,
                "translate-y-0 opacity-100": inView,
              })}
              style={{ transitionDelay: getDelay(4) }}
            >
              <ExpandableSummary summary={entry.summary} />
            </div>

            {!hideChefBio && (
              <div
                className={clsx("transition duration-700 ease-out", {
                  "translate-y-2 opacity-0": !inView,
                  "translate-y-0 opacity-100": inView,
                })}
                style={{ transitionDelay: getDelay(5) }}
              >
                <MiniChefBio chef={entry.chefs?.[0]} />
              </div>
            )}

            {/* If the user is premium, show the recipe actions */}
            {isMobPlusMember && (
              <div
                className={clsx(
                  "RecipeHero__recipeActions",
                  "mt-6 flex space-x-2 lg:space-x-3 print:hidden",
                  "transition duration-700 ease-out",
                  {
                    "translate-y-2 opacity-0": !inView,
                    "translate-y-0 opacity-100": inView,
                  }
                )}
                style={{ transitionDelay: getDelay(6) }}
              >
                <RecipeActions entry={entry} />
              </div>
            )}

            {/* If the user is not premium, show a button to start a free trial */}
            {!isMobPlusMember && (
              <div
                className={clsx(
                  "RecipeHero__recipeActions",
                  "mt-6 flex space-x-2 lg:space-x-3 print:hidden",
                  "transition duration-700 ease-out",
                  {
                    "translate-y-2 opacity-0": !inView,
                    "translate-y-0 opacity-100": inView,
                  }
                )}
                style={{ transitionDelay: getDelay(6) }}
              >
                <SignUpCTAButton componentLocation="details_panel_desktop" />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default DetailsPanel;
